/**
 * Global backup-activity store (Spec 201) — a universal, refresh-surviving record
 * of every backup/restore execution the user kicks off from ANYWHERE in the app.
 *
 * Any entity surface (Storage, Restore, an entity page, …) calls `add()` after a
 * launch; the global Activity drawer renders live status for each entry from any
 * tab. Persisted to localStorage so a page refresh doesn't lose an in-flight job.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type BackupEntityType = 'lakehouse' | 'warehouse' | 'workspace';

export interface ActivityEntry {
  executionName: string;
  verb: 'Backup' | 'Restore' | 'Verify';
  subject: string; // entity display name
  entityType: BackupEntityType;
  entityId: string;
  startedAt: number; // client ms timestamp
  /** Spec 214 FR-026 (Clarify Q5): set when a terminal status is observed — the header
   *  badge counts only NON-terminal entries (the drawer keeps the history list). */
  terminal?: boolean;
}

interface ActivityState {
  entries: ActivityEntry[];
  /** Whether the (global) activity drawer is open. NOT persisted — a refresh shouldn't reopen it. */
  open: boolean;
  /** Record a launch. Auto-opens the drawer so progress is shown immediately, from anywhere. */
  add: (e: Omit<ActivityEntry, 'startedAt'>) => void;
  /** Mark an entry finished (badge drops it; the drawer keeps it as history). */
  markTerminal: (executionName: string) => void;
  remove: (executionName: string) => void;
  clear: () => void;
  setOpen: (open: boolean) => void;
}

/** The badge count: launches still in flight (Clarify Q5 — "Activity" means happening NOW,
 *  not a 30-deep launch history that read as running backups). Entries older than 24h count
 *  as finished even if no drawer ever observed their terminal status (nothing legitimately
 *  runs that long — the backend reaper settles stuck runs at 180 min). */
const MAX_INFLIGHT_AGE_MS = 24 * 60 * 60 * 1000;
export const selectInflightCount = (s: ActivityState): number =>
  s.entries.filter((e) => !e.terminal && Date.now() - e.startedAt < MAX_INFLIGHT_AGE_MS).length;

const MAX_ENTRIES = 30;

export const useBackupActivity = create<ActivityState>()(
  persist(
    (set) => ({
      entries: [],
      open: false,
      add: (e) =>
        set((s) => ({
          open: true, // immediately surface the live-progress drawer wherever the backup was kicked off
          entries: [{ ...e, startedAt: Date.now() }, ...s.entries.filter((x) => x.executionName !== e.executionName)].slice(0, MAX_ENTRIES),
        })),
      markTerminal: (executionName) =>
        set((s) => ({
          entries: s.entries.map((x) => (x.executionName === executionName && !x.terminal ? { ...x, terminal: true } : x)),
        })),
      remove: (executionName) => set((s) => ({ entries: s.entries.filter((x) => x.executionName !== executionName) })),
      clear: () => set({ entries: [] }),
      setOpen: (open) => set({ open }),
    }),
    { name: 'fcp-backup-activity', partialize: (s) => ({ entries: s.entries }) }
  )
);
